import { forwardRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  ZoomIn as iconZoomIn,
  ZoomOut as iconZoomOut,
  ZoomReset as iconZoomReset,
} from '@/shared/ui';
import { ZOOM_SCALE_DEFAULT } from '@/entities/heart-rate';
import SelectedInfo from '../selectedInfo';
import useHrChart from './useHrChart';

export type HrChartHandle = {
  handleClickArrowButton: (isNext: boolean) => void;
};

export type HrChartProps = {
  timezoneOffset: number;
  ecgDataMap: any;
  studyId: string;
  profileId: string;
};

/**
 * Renders the ECG disclosure heart-rate chart of the strips management tab together with its zoom panel. Shows a skeleton while useHrChart reports loading, otherwise draws zoom-in, zoom-out and scale controls plus a reset-zoom button that appears only once chart extremes have been recorded, and mounts the chart container followed by the selected-event info panel.
 *
 * @param props - ECG data map, timezone offset and study context for the chart.
 * @param ref - Forwarded handle the parent uses to drive the chart imperatively.
 * @returns The HR chart wrapper with its zoom panel, chart container and selected-info panel.
 */
const HrChart = forwardRef<HrChartHandle, HrChartProps>(
  ({ timezoneOffset, ecgDataMap, studyId, profileId }, ref) => {
    const {
      isLoadingHrChart,
      zoomScale,
      zoomIndex,
      handleZoomOut,
      handleZoomIn,
      handleResetZoom,
      setExtremesRef,
    } = useHrChart({ timezoneOffset, ecgDataMap, studyId, profileId }, ref);

    return (
      <>
        <div id="hr-chart" className="hr-chart-wrapper-strips-management">
          {isLoadingHrChart ? (
            <div className="hr-chart-skeleton-container-OLD">
              <div className="chart-container">
                <Skeleton height="100%" />
              </div>
            </div>
          ) : (
            <div className="zoom-panel-wrapper">
              <div className="zoom-panel">
                <IconButton
                  id="zoom-in-btn"
                  className="zoom-btn in"
                  iconComponent={<img src={iconZoomOut} alt="zoom out" />}
                  onClick={handleZoomOut}
                />
                <div className="zoom-scale">{`${zoomScale || ZOOM_SCALE_DEFAULT[zoomIndex]}%`}</div>
                <IconButton
                  id="zoom-out-btn"
                  className="zoom-btn out"
                  iconComponent={<img src={iconZoomIn} alt="zoom in" />}
                  onClick={handleZoomIn}
                />
              </div>
              {setExtremesRef.current.length > 0 && (
                <>
                  <IconButton
                    id="resetZoomBtn"
                    className="reset-zoom-btn"
                    iconComponent={<img src={iconZoomReset} alt="zoom reset" />}
                    onClick={handleResetZoom}
                  />
                  <UncontrolledTooltip
                    className="custom-tooltip"
                    target="resetZoomBtn"
                    placement="bottom"
                    delay={{ show: 0, hide: 0 }}
                    hideArrow
                  >
                    Reset zoom
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          )}
          <div
            id="ecg-disclosure-container"
            style={{
              width: '100%',
              height: 150,
              marginTop: 15,
            }}
          />
        </div>
        <SelectedInfo ecgDataMap={ecgDataMap} timezoneOffset={timezoneOffset} />
      </>
    );
  },
);

export default HrChart;
